import { useState } from '#imports'
import { type Ref, computed, ref } from 'vue'

import { useHttpFetch } from '@backmarket/nuxt-module-http-v2/useHttpFetch'
import { useHttpStream } from '@backmarket/nuxt-module-http-v2/useHttpStream'

import type {
  BrowsingContextRequestBody,
  ChatRequestBody,
  MessageRequestBody,
  StreamChunk,
  StreamingEvent,
} from '~/api/shopping-assistant/types'

/**
 * API client for the Shopping Assistant service.
 */
export function useShoppingAssistantApi() {
  const $httpFetch = useHttpFetch()
  let currentSendAbortController: AbortController | null = null
  let currentStreamAbortController: AbortController | null = null
  let currentBrowsingContextAbortController: AbortController | null = null
  const lastSendSignal: Ref<AbortSignal | null> = ref(null)

  function getAmplitudeHeaders(): Record<string, string> {
    const headers: Record<string, string> = {}
    const amplitudeDeviceId = useState<string | undefined>(
      'amplitude-device-id',
    ).value
    const amplitudeSessionId = useState<number | undefined>(
      'amplitude-session-id',
    ).value

    if (amplitudeDeviceId) {
      headers['bm-amplitude-device-id'] = amplitudeDeviceId
    }
    if (amplitudeSessionId) {
      headers['bm-amplitude-session-id'] = amplitudeSessionId.toString()
    }

    return headers
  }

  const onRequest = ({
    options: { headers },
  }: {
    options: { headers: Headers }
  }) => {
    const amplitudeHeaders = getAmplitudeHeaders()
    Object.entries(amplitudeHeaders).forEach(([key, value]) => {
      headers.set(key, value)
    })
  }

  const abortSendMessage = () => {
    currentSendAbortController?.abort()
    currentStreamAbortController?.abort()
    currentStreamAbortController = null
  }

  const abortBrowsingContextPush = () => {
    currentBrowsingContextAbortController?.abort()
  }

  // Replaces the chat's live browsing context (current page). Latest call
  // wins: a new call aborts any push still in flight, so a stale response can
  // never overwrite fresher context.
  const pushBrowsingContext = async (
    chatId: string,
    body: BrowsingContextRequestBody,
  ) => {
    abortBrowsingContextPush()
    const controller = new AbortController()
    currentBrowsingContextAbortController = controller

    try {
      await $httpFetch(
        '/api/shopping-agent/v1/chats/:chatId/browsing-context',
        {
          method: 'PUT',
          body,
          pathParams: { chatId },
          onRequest,
          signal: controller.signal,
        },
      )
    } finally {
      if (currentBrowsingContextAbortController === controller) {
        currentBrowsingContextAbortController = null
      }
    }
  }

  const startChat = async (chatId: string | null, body: ChatRequestBody) => {
    let responseStatus = 0
    abortSendMessage()

    const onResponse = ({ response }: { response: { status: number } }) => {
      responseStatus = response.status
    }

    if (chatId) {
      const data = await $httpFetch('/api/shopping-agent/v1/chats/:chatId', {
        method: 'GET',
        pathParams: { chatId },
        onResponse,
        onRequest,
      })

      return { data, status: responseStatus }
    }

    const data = await $httpFetch('/api/shopping-agent/v1/chats', {
      method: 'POST',
      body,
      onResponse,
      onRequest,
    })

    return { data, status: responseStatus }
  }

  const sendMessage = async (chatId: string, body: MessageRequestBody) => {
    const controller = new AbortController()
    currentSendAbortController = controller
    lastSendSignal.value = controller.signal

    try {
      return await $httpFetch('/api/shopping-agent/v1/chats/:chatId/message', {
        method: 'POST',
        body,
        pathParams: { chatId },
        onRequest,
        signal: controller.signal,
      })
    } finally {
      currentSendAbortController = null
    }
  }

  const streamMessage = (
    chatId: string,
    body: MessageRequestBody,
    callbacks: {
      onEvent?: (event: StreamChunk) => void
      onEnd?: (terminatedBySentinel: boolean) => void
      onAbort?: () => void
      onError?: (error: Error) => void
    },
  ) => {
    const $httpStream = useHttpStream()
    const controller = new AbortController()
    currentStreamAbortController = controller

    const streamPromise = $httpStream(
      '/api/shopping-agent/v1/chats/:chatId/message',
      {
        method: 'POST',
        pathParams: { chatId },
        headers: {
          'Content-Type': 'application/json',
          ...getAmplitudeHeaders(),
        },
        body: body as unknown as Record<string, unknown>,
        signal: controller.signal,
        onEvent: callbacks.onEvent
          ? (event: unknown) => {
              if (controller.signal.aborted) return

              callbacks.onEvent?.(event as StreamChunk)
            }
          : undefined,
        onEnd: callbacks.onEnd,
        onAbort: callbacks.onAbort,
        onError: callbacks.onError,
      },
    )

    streamPromise.catch((err: Error) => {
      if (err.name === 'AbortError') {
        callbacks.onAbort?.()

        return
      }
      if (!callbacks.onError) return
      const match = err.message.match(/HTTP Stream Error: (\d+)/)
      callbacks.onError(
        match
          ? Object.assign(new Error(err.message), {
              status: parseInt(match[1], 10),
            })
          : err,
      )
    })

    return streamPromise
  }

  const streamStartChat = (
    chatId: string | null,
    body: ChatRequestBody,
    callbacks: {
      onEvent?: (event: StreamingEvent) => void
      onEnd?: (terminatedBySentinel: boolean) => void
      onAbort?: () => void
      onError?: (error: Error) => void
    },
  ) => {
    const $httpStream = useHttpStream()
    const controller = new AbortController()
    currentStreamAbortController = controller

    const onEvent = callbacks.onEvent
      ? (event: unknown) => {
          if (controller.signal.aborted) return

          callbacks.onEvent?.(event as StreamingEvent)
        }
      : undefined

    const streamPromise = chatId
      ? $httpStream('/api/shopping-agent/v1/chats/:chatId', {
          method: 'GET',
          pathParams: { chatId },
          headers: getAmplitudeHeaders(),
          signal: controller.signal,
          onEvent,
          onEnd: callbacks.onEnd,
          onAbort: callbacks.onAbort,
          onError: callbacks.onError,
        })
      : $httpStream('/api/shopping-agent/v1/chats', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...getAmplitudeHeaders(),
          },
          body: body as unknown as Record<string, unknown>,
          signal: controller.signal,
          onEvent,
          onEnd: callbacks.onEnd,
          onAbort: callbacks.onAbort,
          onError: callbacks.onError,
        })

    streamPromise.catch((err: Error) => {
      if (err.name === 'AbortError') {
        callbacks.onAbort?.()

        return
      }
      if (!callbacks.onError) return
      const match = err.message.match(/HTTP Stream Error: (\d+)/)
      callbacks.onError(
        match
          ? Object.assign(new Error(err.message), {
              status: parseInt(match[1], 10),
            })
          : err,
      )
    })

    return streamPromise
  }

  return {
    startChat,
    sendMessage,
    abortSendMessage,
    isSendMessageAborted: computed(
      () => lastSendSignal.value?.aborted ?? false,
    ),
    streamMessage,
    streamStartChat,
    pushBrowsingContext,
    abortBrowsingContextPush,
  }
}
